import './index.css';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useRef, useCallback, cloneElement } from 'react';
import classnames from 'classnames';
import { keyboardMatches, keyboardKeys } from '@hh.ru/magritte-common-keyboard';
import { CellText, Cell } from '@hh.ru/magritte-ui-cell';
import { Drop } from '@hh.ru/magritte-ui-drop';
import { Loader } from '@hh.ru/magritte-ui-loader';
import { SelectError } from './SelectError.js';
import { SelectOption } from './SelectOption.js';
import { getChecked } from './getChecked.js';
import '@hh.ru/magritte-ui-breakpoint';
import '@hh.ru/magritte-ui-button';
import '@hh.ru/magritte-ui-icon/variants/icon';
import '@hh.ru/magritte-ui-spacing';
import '@hh.ru/magritte-ui-title';
import '@hh.ru/magritte-ui-typography';
import '@hh.ru/magritte-ui-checkable-card/CheckableCardElement';
import '@hh.ru/magritte-ui-checkbox-radio';
import '@hh.ru/magritte-ui-icon/icon';

var styles = {"select-drop-container-wrapper":"magritte-select-drop-container-wrapper___OHM0q_10-0-16","selectDropContainerWrapper":"magritte-select-drop-container-wrapper___OHM0q_10-0-16","select-drop-container":"magritte-select-drop-container___hee1p_10-0-16","selectDropContainer":"magritte-select-drop-container___hee1p_10-0-16","select-drop-container_plain":"magritte-select-drop-container_plain___766zl_10-0-16","selectDropContainerPlain":"magritte-select-drop-container_plain___766zl_10-0-16","select-drop":"magritte-select-drop___jn4O-_10-0-16","selectDrop":"magritte-select-drop___jn4O-_10-0-16","select-drop-header":"magritte-select-drop-header___fMmio_10-0-16","selectDropHeader":"magritte-select-drop-header___fMmio_10-0-16","select-drop_plain":"magritte-select-drop_plain___VL8Fz_10-0-16","selectDropPlain":"magritte-select-drop_plain___VL8Fz_10-0-16","label-option-plain":"magritte-label-option-plain___BvBH8_10-0-16","labelOptionPlain":"magritte-label-option-plain___BvBH8_10-0-16","loading":"magritte-loading___AMiuq_10-0-16","hint":"magritte-hint___6qaFl_10-0-16"};

const SelectOptionDefault = ({ data, input, type, plain, }) => {
    if (type === 'delimiter') {
        return (jsx(CellText, { maxLines: 1, style: "secondary", children: data.text }));
    }
    if (type === 'label') {
        if (plain) {
            return jsx(CellText, { maxLines: 1, children: data.text });
        }
        return (jsx(Cell, { Element: "div", align: "center", children: jsx("div", { className: styles.labelOptionPlain, children: jsx(CellText, { maxLines: 1, children: data.text }) }) }));
    }
    return (jsx(Cell, { Element: "div", align: "center", left: input, children: jsx(CellText, { maxLines: 1, children: data.text }) }));
};
const RenderButtonsContainerDefault = ({ applyChangesButton }) => {
    const primaryActions = [applyChangesButton].filter((btn) => btn);
    if (primaryActions.length === 0) {
        return null;
    }
    // заменить на action-bar, когда разберемся с отсутпами
    return jsx(Fragment, { children: applyChangesButton });
};
const SelectDrop = ({ activatorRef, type, name, multiple, applyChangesButton: _applyChangesButton, clearButton: _clearButton, useInitialValuesOnClearAction, loading, renderItem = SelectOptionDefault, renderButtonsContainer = RenderButtonsContainerDefault, visible, optionList, optionsListRef, searchValue, setSearchValue, maxWidth, value: values, onChange, onClose, clearSelectedValues, setInitialValues, isLimitExceeded, plain, searchable, widthEqualToActivator, forceDropVisibleWithEmptyOptions, renderContentBefore, setForcedState, setSelectedValues, maxHeight, onDropClose, onDropOpen, emptyOptionsHint, emptyOptionsSearchHint, onApply, onClear, searchInProgress, internalError, reload, innerErrorTrls, optionDataQa, ...props }) => {
    const closedByKeyboardRef = useRef(false);
    const handleChange = useCallback((newValue, needToPut) => {
        onChange(newValue, needToPut);
        if (!multiple) {
            onClose?.();
        }
        if (!multiple && closedByKeyboardRef.current) {
            closedByKeyboardRef.current = false;
            setForcedState('focus');
            return;
        }
        if (multiple && searchValue) {
            setForcedState('focusAndSelect');
            return;
        }
        closedByKeyboardRef.current = false;
        setForcedState(null);
    }, [multiple, onChange, onClose, searchValue, setForcedState]);
    const clearSelectedValuesAndClose = () => {
        if (useInitialValuesOnClearAction) {
            setInitialValues();
        }
        else {
            clearSelectedValues();
        }
        onClose?.();
    };
    const clearButton = _clearButton &&
        cloneElement(_clearButton, {
            key: 'clearButton',
            size: 'medium',
            'data-qa': 'magritte-select-clear-action',
            onClick: () => {
                clearSelectedValuesAndClose();
                onClear?.();
            },
        });
    const needAddChangesButton = multiple && _applyChangesButton;
    const applyChangesButton = (needAddChangesButton &&
        cloneElement(_applyChangesButton, {
            key: 'applyChangesButton',
            size: 'medium',
            'data-qa': 'magritte-select-apply',
            onClick: () => {
                setSelectedValues();
                onClose?.();
                onApply?.({ isMobile: false, searchValue });
            },
        })) ||
        undefined;
    const footer = !loading &&
        needAddChangesButton &&
        renderButtonsContainer({
            applyChangesButton,
            clearButton,
        });
    const emptyOptionsHintVisible = optionList.length === 0 &&
        !searchValue &&
        !!emptyOptionsHint &&
        !loading &&
        !searchInProgress &&
        !internalError;
    const emptySearchOptionsHintVisible = !!(optionList.length === 0 &&
        searchValue &&
        !!emptyOptionsSearchHint &&
        !loading &&
        !searchInProgress &&
        !internalError);
    const optionsVisible = !loading && !emptySearchOptionsHintVisible && !emptyOptionsHintVisible && !internalError;
    return (jsx(Drop, { ...props, ref: optionsListRef, autoFocusWhenOpened: false, maxWidth: maxWidth, visible: (optionList.length > 0 ||
            forceDropVisibleWithEmptyOptions ||
            emptyOptionsHintVisible ||
            emptySearchOptionsHintVisible ||
            internalError) &&
            visible, placement: ['bottom-left', 'top-left'], activatorRef: activatorRef, role: searchable ? 'status' : 'listbox', space: plain ? 600 : 300, keyboardNavigationMode: "list", onClose: () => {
            setForcedState('focus');
            onClose?.();
        }, closeByClickOutside: false, onKeyDownCapture: (event) => {
            if (keyboardMatches(event.nativeEvent, [keyboardKeys.Enter, keyboardKeys.Space])) {
                closedByKeyboardRef.current = true;
            }
        }, widthEqualToActivator: widthEqualToActivator || loading, forcePlacement: true, footer: footer, onAfterExit: onDropClose, onAppear: onDropOpen, children: jsx("div", { className: styles.selectDropContainerWrapper, style: { maxHeight: `${maxHeight}px` }, children: jsxs("div", { className: classnames(styles.selectDropContainer, { [styles.selectDropContainerPlain]: plain }), children: [internalError && innerErrorTrls && jsx(SelectError, { innerErrorTrls: innerErrorTrls, reload: reload }), emptyOptionsHintVisible && jsx("div", { className: styles.hint, children: emptyOptionsHint }), emptySearchOptionsHintVisible && jsx("div", { className: styles.hint, children: emptyOptionsSearchHint }), loading && (jsx("div", { className: styles.loading, children: jsx(Loader, { size: 24 }) })), optionsVisible && (jsxs(Fragment, { children: [renderContentBefore && (jsx("div", { "data-qa": "magritte-select-header", className: styles.selectDropHeader, children: renderContentBefore({ clearSelectedValuesAndClose, searchValue, setSearchValue }) })), jsx("div", { className: classnames(styles.selectDrop, { [styles.selectDropPlain]: plain }), "data-qa": "magritte-select-option-list", children: optionList.map((option) => {
                                    const checked = Boolean(option.data && getChecked(option.data, values ?? []));
                                    const disabled = !checked && (option.disabled || isLimitExceeded);
                                    return (jsx(SelectOption, { name: name, checked: checked, onChange: handleChange, render: renderItem, disabled: disabled, type: option.type || type, plain: !!plain, isMobile: false, multiple: multiple, "data-qa": optionDataQa, data: option.data }, option.data.value));
                                }) })] }))] }) }) }));
};

export { SelectDrop };
//# sourceMappingURL=SelectDrop.js.map
